import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenAcsVisitsIndicatorC3Data } from "@/services/esus/indicator/esf/c3/getDetailedCitizensAcsVisitsIndicatorC3";
import { getCitizenScoreStatusDescriptionIndicatorC3 } from "@/utils/indicatorsEsf";

import { IndicatorEsfC3CitizensAcsVisitsFilters } from "./IndicatorEsfC3CitizensAcsVisitsFilters";
import { IndicatorEsfC3CitizensAcsVisitsTable } from "./IndicatorEsfC3CitizensAcsVisitsTable";
import { IndicatorEsfC3CitizensAcsVisitsHistory } from "./IndicatorEsfC3CitizensAcsVisitsHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC3CitizensAcsVisitsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensAcsVisits: Paginated<DetailedCitizenAcsVisitsIndicatorC3Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensAcsVisitsIndicatorC3: () => Promise<
		DetailedCitizenAcsVisitsIndicatorC3Data[]
	>;
};

export function IndicatorEsfC3CitizensAcsVisits({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensAcsVisits,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensAcsVisitsIndicatorC3: handleGetAllDetailedIndicatorC1,
}: Readonly<IndicatorEsfC3CitizensAcsVisitsProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithAcsVisits, setCitizenWithAcsVisits] =
		useState<DetailedCitizenAcsVisitsIndicatorC3Data>(
			new DetailedCitizenAcsVisitsIndicatorC3Data()
		);
	const [isLoadingCitizenWithAcsHomeVisits, setIsLoadingCitizenWithAcsHomeVisits] =
		useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAcsVisits(
		citizenWithHomeVisits: DetailedCitizenAcsVisitsIndicatorC3Data
	) {
		setIsLoadingCitizenWithAcsHomeVisits(true);
		setCitizenWithAcsVisits(citizenWithHomeVisits);
		setIsLoadingCitizenWithAcsHomeVisits(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedIndicatorC1();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformCitizensWithAcsVisitsToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Visitas ACS - ${dayjs(quarterDates.startDate).format("DD-MM-YYYY")} à ${dayjs(
					quarterDates.endDate
				).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformCitizensWithAcsVisitsToGenericObjectArray(
		citizensWithAcsVisits: DetailedCitizenAcsVisitsIndicatorC3Data[]
	) {
		return citizensWithAcsVisits.map(
			(citizenWithAcsVisits) =>
				({
					Nome: citizenWithAcsVisits.fullName,
					DN: dayjs(citizenWithAcsVisits.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenWithAcsVisits.birthdate),
					Documento: citizenWithAcsVisits.cpf ?? citizenWithAcsVisits.cns ?? "",
					Equipe: citizenWithAcsVisits.healthTeam?.name ?? "",
					Ine: citizenWithAcsVisits.healthTeam?.ine ?? "",
					Microarea: citizenWithAcsVisits.area ?? "FA",
					Dum: dayjs(citizenWithAcsVisits.dumDate).format("DD/MM/YYYY"),
					Dpp: dayjs(citizenWithAcsVisits.dppDate).format("DD/MM/YYYY"),
					IG: citizenWithAcsVisits.gestationalAge ?? "",
					"Visitas - gestação": citizenWithAcsVisits.acsHomeVisits?.length ?? 0,
					"Status visitas - gestação": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithAcsVisits.threeAcsHomeVisitsStatus
					),
					"Visitas - puerpério": citizenWithAcsVisits.puerperalAcsHomeVisits?.length ?? 0,
					"Status visitas - puerpério": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithAcsVisits.puerperalAcsHomeVisitStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithAcsHomeVisits || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC3CitizensAcsVisitsFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC3CitizensAcsVisitsTable
							citizensAcsVisits={paginatedCitizensAcsVisits.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAcsVisits}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensAcsVisits?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensAcsVisits.pageSize}
								totalPages={paginatedCitizensAcsVisits.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Visitas dos ACS
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC3CitizensAcsVisitsHistory
							citizenWithAcsHomeVisits={citizenWithAcsVisits}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
